<script setup lang="ts">
import { computed, ref } from "vue";
import type { ToolRoundTrip } from "../types/chat";

const props = defineProps<{ trip: ToolRoundTrip }>();

const open = ref(false);

const isRunSql = computed(() => props.trip.name === "run_sql");
const isSearch = computed(() => props.trip.name === "search_content");
const isBrave = computed(() => props.trip.name === "brave_web_search");
const isSearchUploaded = computed(
  () => props.trip.name === "search_uploaded_docs",
);
const isReadUploaded = computed(() => props.trip.name === "read_uploaded_doc");
const isFindProfiles = computed(() => props.trip.name === "find_profiles");
const isCreateDocument = computed(() => props.trip.name === "create_document");
const status = computed<"running" | "ok" | "error">(() => {
  if (!props.trip.result) return "running";
  return props.trip.result.ok ? "ok" : "error";
});

/** Headline: short, human-readable status string surfaced even when the card
 * is collapsed. No raw tool / arg names — those leak implementation detail
 * into the user UI. The icon column carries the type signal. */
const headline = computed<string>(() => {
  const args = props.trip.arguments ?? {};
  if (isRunSql.value) {
    if (status.value === "error") {
      return sqlErrorLabel.value.headline;
    }
    const payload = props.trip.result?.payload ?? {};
    const n = (payload.row_count as number | undefined) ?? null;
    if (n === null) return "Running query…";
    return `Looked up data · ${n} row${n === 1 ? "" : "s"}`;
  }
  if (isSearch.value) {
    const q = String(args.query ?? "").slice(0, 80);
    if (status.value === "error") return `Search failed: ${q}`;
    if (status.value === "running") return `Searching "${q}"…`;
    const payload = props.trip.result?.payload ?? {};
    const n = (payload.hits as unknown[] | undefined)?.length ?? 0;
    return `Searched "${q}" · ${n} hit${n === 1 ? "" : "s"}`;
  }
  if (isBrave.value) {
    const q = String(args.query ?? "").slice(0, 80);
    if (status.value === "error") return `Web search failed: ${q}`;
    if (status.value === "running") return `Searching the web for "${q}"…`;
    const payload = props.trip.result?.payload ?? {};
    const n = (payload.hits as unknown[] | undefined)?.length ?? 0;
    return `Web search · "${q}" · ${n} result${n === 1 ? "" : "s"}`;
  }
  if (isSearchUploaded.value) {
    const q = String(args.query ?? "").slice(0, 80);
    if (status.value === "running") return `Searching your uploads for "${q}"…`;
    if (status.value === "error") return `Upload search failed: ${q}`;
    const payload = props.trip.result?.payload ?? {};
    const n = (payload.hits as unknown[] | undefined)?.length ?? 0;
    return `Searched your uploads · "${q}" · ${n} hit${n === 1 ? "" : "s"}`;
  }
  if (isReadUploaded.value) {
    const payload = props.trip.result?.payload ?? {};
    const title = String(payload.title ?? args.source ?? "uploaded file");
    if (status.value === "running") return `Opening ${title}…`;
    if (status.value === "error") return `Couldn't read ${title}`;
    const truncated = Boolean(payload.truncated);
    const tokens = Number(payload.estimated_tokens ?? 0);
    return truncated
      ? `Read "${title}" (truncated; ~${tokens.toLocaleString()} tokens)`
      : `Read "${title}" (~${tokens.toLocaleString()} tokens)`;
  }
  if (isFindProfiles.value) {
    const q = String(args.query ?? "").slice(0, 80);
    if (status.value === "running") return `Looking up profiles for "${q}"…`;
    if (status.value === "error") return `Profile lookup failed: ${q}`;
    const payload = props.trip.result?.payload ?? {};
    const n = (payload.hits as unknown[] | undefined)?.length ?? 0;
    return `Found ${n} consultant${n === 1 ? "" : "s"} matching "${q}"`;
  }
  if (isCreateDocument.value) {
    // The download card next to this already shows WHAT was built, so the
    // line says what HAPPENED. Without a case here it fell through to
    // "Looked something up", which describes a search and is wrong for the
    // one tool that does not search.
    const payload = props.trip.result?.payload ?? {};
    const kind = String(payload.kind ?? args.kind ?? "");
    const noun =
      kind === "cv" ? "CV" : kind === "deck" ? "presentation" : "document";
    if (status.value === "running") return `Building the ${noun}…`;
    if (status.value === "error") return `Could not build the ${noun}`;
    const fmt = String(payload.format ?? "").toUpperCase();
    return fmt ? `Built the ${noun} · ${fmt}` : `Built the ${noun}`;
  }
  // Last-resort default — keep it neutral, no developer-y tool name.
  return status.value === "running" ? "Working…" : "Looked something up";
});

/** Pretty-print SQL with light formatting for the expanded view. */
const sqlPretty = computed<string>(() => {
  const sql = String(
    props.trip.result?.payload?.sql ?? props.trip.arguments?.sql ?? "",
  );
  return sql
    .replace(/\s+/g, " ")
    .replace(
      /\b(SELECT|FROM|WHERE|GROUP BY|ORDER BY|LIMIT|JOIN|ON|HAVING)\b/gi,
      "\n$1",
    )
    .trim();
});

const sqlRows = computed(() => {
  const p = props.trip.result?.payload ?? {};
  return {
    columns: (p.columns as string[] | undefined) ?? [],
    rows: (p.rows_preview as Record<string, unknown>[] | undefined) ?? [],
    total: (p.row_count as number | undefined) ?? 0,
  };
});

const errorMessage = computed<string>(() => {
  const p = props.trip.result?.payload ?? {};
  return String(p.error ?? "");
});

/** "Refused" is only true when OUR safety gate stopped the query. Saying it
 *  for a database error blames the wrong thing — a reviewer reading the card
 *  concludes the platform blocked a legitimate query, when in fact Postgres
 *  rejected the model's SQL and the model can simply fix it. */
const sqlErrorLabel = computed<{ headline: string; heading: string }>(() => {
  const kind = String(props.trip.result?.payload?.kind ?? "");
  switch (kind) {
    case "sql_safety":
      return {
        headline: "Query refused",
        heading: "Why the query was refused",
      };
    case "sql_timeout":
      return {
        headline: "Query took too long",
        heading: "Why the query was stopped",
      };
    case "rls_unenforceable":
      return {
        headline: "Query refused",
        heading: "Why the query was refused",
      };
    case "sql_error":
      return {
        headline: "Database rejected the query",
        heading: "What the database said",
      };
    default:
      return { headline: "Query failed", heading: "What went wrong" };
  }
});
</script>

<template>
  <details
    class="tool-card"
    :open="open"
    @toggle="(e) => (open = (e.target as HTMLDetailsElement).open)"
  >
    <summary class="tool-card__summary" :data-status="status">
      <span class="tool-card__icon" aria-hidden="true">
        <template v-if="isRunSql">▦</template>
        <template v-else-if="isSearch">⌕</template>
        <template v-else-if="isBrave">🌐</template>
        <template v-else-if="isSearchUploaded">📎</template>
        <template v-else-if="isReadUploaded">📄</template>
        <template v-else-if="isFindProfiles">👤</template>
        <template v-else>·</template>
      </span>
      <span class="tool-card__headline">{{ headline }}</span>
      <span
        v-if="status === 'running'"
        class="tool-card__spinner"
        aria-hidden="true"
        >…</span
      >
      <span
        v-else-if="status === 'error'"
        class="tool-card__badge tool-card__badge--err"
      >
        error
      </span>
      <span class="tool-card__chevron" aria-hidden="true">▾</span>
    </summary>

    <div class="tool-card__body">
      <!-- run_sql expanded view: SQL block + result preview table -->
      <template v-if="isRunSql">
        <h5 class="tool-card__h">SQL</h5>
        <pre class="tool-card__sql"><code>{{ sqlPretty }}</code></pre>
        <template v-if="status === 'ok'">
          <h5 class="tool-card__h">
            Result
            <span
              v-if="sqlRows.total > sqlRows.rows.length"
              class="tool-card__more"
            >
              (showing {{ sqlRows.rows.length }} of {{ sqlRows.total }})
            </span>
          </h5>
          <div class="tool-card__table-wrap">
            <table class="tool-card__table">
              <thead>
                <tr>
                  <th v-for="c in sqlRows.columns" :key="c">{{ c }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in sqlRows.rows" :key="idx">
                  <td
                    v-for="c in sqlRows.columns"
                    :key="c"
                    :title="String(row[c] ?? '')"
                  >
                    {{ row[c] === null ? "—" : row[c] }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
        <template v-else-if="status === 'error'">
          <h5 class="tool-card__h">{{ sqlErrorLabel.heading }}</h5>
          <p class="tool-card__err">{{ errorMessage }}</p>
          <p class="tool-card__hint">
            The model can retry with a corrected query — its next attempt will
            show up as a new card below.
          </p>
        </template>
      </template>

      <!-- search_content expanded view: just the query in plain text. -->
      <template v-else-if="isSearch">
        <h5 class="tool-card__h">Search query</h5>
        <p class="tool-card__query">{{ trip.arguments.query }}</p>
        <p v-if="status === 'ok'" class="tool-card__hint">
          Results are listed in the <strong>sources</strong> section below the
          answer; click any [N] chip in the answer to jump to it.
        </p>
        <p v-else-if="status === 'error'" class="tool-card__err">
          {{ errorMessage }}
        </p>
      </template>

      <!-- brave_web_search expanded view -->
      <template v-else-if="isBrave">
        <h5 class="tool-card__h">Searched the web for</h5>
        <p class="tool-card__query">{{ trip.arguments.query }}</p>
        <p v-if="status === 'ok'" class="tool-card__hint">
          Web results are listed in the <strong>sources</strong> section below
          the answer. Clicking a [N] citation opens the source URL in a new tab.
          Only the query above was sent to Brave — your conversation and uploads
          stayed on this platform.
        </p>
        <p v-else-if="status === 'error'" class="tool-card__err">
          {{ errorMessage }}
        </p>
      </template>

      <!-- search_uploaded_docs expanded view -->
      <template v-else-if="isSearchUploaded">
        <h5 class="tool-card__h">Searched your uploads for</h5>
        <p class="tool-card__query">{{ trip.arguments.query }}</p>
        <p v-if="status === 'ok'" class="tool-card__hint">
          Matching passages from your uploaded files appear in the
          <strong>sources</strong> section below the answer.
        </p>
        <p v-else-if="status === 'error'" class="tool-card__err">
          {{ errorMessage }}
        </p>
      </template>

      <!-- read_uploaded_doc expanded view -->
      <template v-else-if="isReadUploaded">
        <h5 class="tool-card__h">File</h5>
        <p class="tool-card__query">
          {{ trip.result?.payload?.title ?? trip.arguments.source }}
        </p>
        <p
          v-if="status === 'ok' && trip.result?.payload?.truncated"
          class="tool-card__hint"
        >
          This file is over the read-whole budget — the assistant got the first
          ~100k tokens. For specific passages it can use the “search your
          uploads” tool instead.
        </p>
        <p v-else-if="status === 'ok'" class="tool-card__hint">
          The assistant read the full file body to answer.
        </p>
        <p v-else-if="status === 'error'" class="tool-card__err">
          {{ errorMessage }}
        </p>
      </template>

      <!-- find_profiles expanded view -->
      <template v-else-if="isFindProfiles">
        <h5 class="tool-card__h">Looked up consultants matching</h5>
        <p class="tool-card__query">{{ trip.arguments.query }}</p>
        <p v-if="status === 'ok'" class="tool-card__hint">
          Profile matches appear in the <strong>sources</strong> section below
          the answer.
        </p>
        <p v-else-if="status === 'error'" class="tool-card__err">
          {{ errorMessage }}
        </p>
      </template>

      <!-- Generic fallback for any tool we haven't taught the UI yet.
           Plain-language summary; we don't dump JSON to the user. -->
      <template v-else>
        <p class="tool-card__hint">
          The assistant used a helper tool to look something up. Sources it
          found appear below the answer.
        </p>
      </template>
    </div>
  </details>
</template>

<style scoped>
.tool-card {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface-alt, var(--color-surface));
  font-size: 0.875rem;
}
.tool-card__summary {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  cursor: pointer;
  list-style: none;
  user-select: none;
}
.tool-card__summary::-webkit-details-marker {
  display: none;
}
.tool-card__summary[data-status="running"] {
  color: var(--color-muted);
}
.tool-card__summary[data-status="error"] {
  color: var(--color-error);
}
.tool-card__icon {
  display: inline-flex;
  width: 1.25rem;
  justify-content: center;
  opacity: 0.7;
}
.tool-card__headline {
  flex: 1;
  /* Plain prose — monospace makes user-facing strings look like
   * debug output. We use the body font instead. */
}
.tool-card__query {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: var(--space-2);
  margin: 0;
  font-style: italic;
}
.tool-card__spinner {
  font-weight: bold;
}
.tool-card__badge {
  font-size: 0.75rem;
  padding: 0.125rem 0.375rem;
  border-radius: var(--radius-sm);
}
.tool-card__badge--err {
  background: color-mix(in srgb, var(--color-error) 12%, transparent);
  color: var(--color-error);
}
.tool-card__chevron {
  transition: transform 0.15s ease;
  opacity: 0.6;
}
.tool-card[open] .tool-card__chevron {
  transform: rotate(180deg);
}
.tool-card__body {
  padding: var(--space-2) var(--space-3) var(--space-3);
  border-top: 1px solid var(--color-border);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.tool-card__h {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--color-muted);
}
.tool-card__more {
  font-weight: 400;
  text-transform: none;
  margin-left: var(--space-2);
}
.tool-card__sql {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: var(--space-2);
  font-family: var(--font-mono, monospace);
  font-size: 0.8125rem;
  white-space: pre-wrap;
  overflow-x: auto;
  margin: 0;
}
.tool-card__table-wrap {
  overflow-x: auto;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
}
.tool-card__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
}
.tool-card__table th,
.tool-card__table td {
  padding: var(--space-1) var(--space-2);
  text-align: left;
  border-bottom: 1px solid var(--color-border);
  white-space: nowrap;
  max-width: 24ch;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tool-card__table th {
  background: var(--color-surface-alt, var(--color-surface));
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-muted);
}
.tool-card__table tbody tr:last-child td {
  border-bottom: 0;
}
.tool-card__err {
  margin: 0;
  font-family: var(--font-mono, monospace);
  color: var(--color-error);
}
.tool-card__hint {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--color-muted);
}
</style>
